import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, Cell } from 'recharts';
import Card from '../common/Card';
import { analyticsAPI } from '../../api/endpoints';
import { RefreshCw } from 'lucide-react';

const COLORS = [
  '#3B82F6', // Blue
  '#10B981', // Green
  '#F59E0B', // Amber
  '#EF4444', // Red
  '#8B5CF6', // Purple
  '#EC4899', // Pink
  '#14B8A6', // Teal
  '#F97316', // Orange
  '#6366F1', // Indigo
  '#84CC16', // Lime
];

const formatLabel = (field: any) => {
  if (!field) return '';
  if (typeof field === 'object' && field.label) return field.label;
  const str = typeof field === 'object' ? field.name : field;
  return str
    .split('_')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
};

const CrossTabulationCard = ({ eventId, analyzableFields }) => {
  // If there are less than 2 fields, we can't do cross-tabulation
  if (!analyzableFields || analyzableFields.length < 2) {
    return null;
  }

  // Default to the first two fields
  const [field1, setField1] = useState(typeof analyzableFields[0] === 'object' ? analyzableFields[0].name : analyzableFields[0]);
  const [field2, setField2] = useState(typeof analyzableFields[1] === 'object' ? analyzableFields[1].name : analyzableFields[1]);

  const { data: crossTabData, isLoading, isError } = useQuery({
    queryKey: ['cross-tabulation', eventId, field1, field2],
    queryFn: async () => {
      if (!eventId || !field1 || !field2) return null;
      const response = await analyticsAPI.getCrossTabulation(eventId, field1, field2);
      return response.data;
    },
    enabled: !!eventId && !!field1 && !!field2,
  });

  return (
    <Card className="shadow-sm border-gray-200 mt-8 mb-6">
      <Card.Content className="p-6">
        <div className="flex flex-col md:flex-row items-start md:items-center justify-between mb-6 gap-4">
          <div>
            <h3 className="text-xl font-bold text-gray-900">Cross-Tabulation Explorer</h3>
            <p className="text-sm text-gray-500 mt-1">Compare two custom fields to find intersecting trends</p>
          </div>
          
          <div className="flex flex-col sm:flex-row items-center gap-3 w-full md:w-auto">
            <select
              value={field1}
              onChange={(e) => setField1(e.target.value)}
              className="w-full sm:w-auto px-3 py-2 border border-gray-300 rounded-lg text-sm bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              {analyzableFields.map((f: any) => {
                const val = typeof f === 'object' ? f.name : f;
                return (
                  <option key={`f1-${val}`} value={val} disabled={val === field2}>{formatLabel(f)}</option>
                );
              })}
            </select>
            <span className="text-gray-400 font-medium text-sm">VS</span>
            <select
              value={field2}
              onChange={(e) => setField2(e.target.value)}
              className="w-full sm:w-auto px-3 py-2 border border-gray-300 rounded-lg text-sm bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              {analyzableFields.map((f: any) => {
                const val = typeof f === 'object' ? f.name : f;
                return (
                  <option key={`f2-${val}`} value={val} disabled={val === field1}>{formatLabel(f)}</option>
                );
              })}
            </select>
          </div>
        </div>

        {isLoading ? (
          <div className="h-[400px] flex items-center justify-center">
            <RefreshCw className="h-8 w-8 text-blue-500 animate-spin" />
          </div>
        ) : isError || !crossTabData ? (
          <div className="h-[400px] flex items-center justify-center text-gray-500">
            Failed to load cross-tabulation data.
          </div>
        ) : crossTabData.data.length === 0 ? (
          <div className="h-[400px] flex items-center justify-center text-gray-500">
            No intersecting data found for these fields.
          </div>
        ) : (
          <>
            <div className="h-[400px]">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart
                  data={crossTabData.data}
                  margin={{ top: 20, right: 30, left: 20, bottom: 60 }}
                >
                  <CartesianGrid strokeDasharray="3 3" vertical={false} />
                  <XAxis 
                    dataKey="name" 
                    angle={-45}
                    textAnchor="end"
                    height={80}
                    tick={{ fill: '#6B7280', fontSize: 12 }}
                    tickMargin={5}
                  />
                  <YAxis tick={{ fill: '#6B7280', fontSize: 12 }} />
                  <Tooltip
                    contentStyle={{ borderRadius: '8px', border: 'none', boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1), 0 2px 4px -2px rgb(0 0 0 / 0.1)' }}
                  />
                  <Legend wrapperStyle={{ paddingTop: '20px' }} />
                  
                  {crossTabData.field2Keys.map((key, index) => (
                    <Bar 
                      key={key} 
                      dataKey={key} 
                      stackId="a" 
                      fill={COLORS[index % COLORS.length]} 
                    />
                  ))}
                </BarChart>
              </ResponsiveContainer>
            </div>
            
            <div className="mt-4 p-4 bg-blue-50 rounded-lg border border-blue-100">
              <p className="text-sm text-blue-800">
                <span className="font-semibold">Insight:</span> This chart shows the distribution of <span className="font-semibold">{crossTabData.field2.label}</span> grouped by each <span className="font-semibold">{crossTabData.field1.label}</span>. Hover over the bars to see exact counts.
              </p>
            </div>
          </>
        )}
      </Card.Content>
    </Card>
  );
};

export default CrossTabulationCard;
